import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateFieldCropPractice, updateFieldCropPracticeAdmin, updateFieldCropPracticeSimulations } from "services";

import { getEnvironment, invalidateCarbonProgramQueriesByChunk, invalidateQueriesByChunk } from "utils";

import {
  GET_FARM_DATA_SUBMISSION_STATUS,
  GET_FARM_REPORTING_YEAR,
  GET_FIELDS_CROP_RECOMMENDATIONS,
  GET_FIELD_PRACTICES,
  GET_FIELD_PRACTICE_FILTER_OPTIONS,
  GET_FIELD_TASKS,
  GET_INCENTIVES_POTENTIAL_PAYOUTS,
  GET_VERIFICATION_EDITING_REQUESTS,
  UPDATE_FIELD_CROP_PRACTICE,
} from "consts";

import { CropPracticesRequestBody } from "models";

type MutateParams = {
  fieldId: string;
  cropId: string;
  simulationId?: string;
  requestBody: CropPracticesRequestBody;
};

export const useUpdateFieldCropPractice = ({ fieldId, farmId, simulationId }: { fieldId: string; farmId?: string; simulationId?: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_FIELD_CROP_PRACTICE, fieldId, simulationId],
    mutationFn: (data: Pick<MutateParams, "cropId" | "requestBody">) => mutate({ fieldId, simulationId, ...data }),
    onSuccess: () => {
      invalidateCarbonProgramQueriesByChunk(fieldId, simulationId);
      queryClient.invalidateQueries({ queryKey: [GET_INCENTIVES_POTENTIAL_PAYOUTS] });
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICE_FILTER_OPTIONS] });
      if (!simulationId) {
        invalidateQueriesByChunk(GET_FIELD_PRACTICES, fieldId);
        queryClient.invalidateQueries({ queryKey: [GET_FIELDS_CROP_RECOMMENDATIONS, [fieldId]] });
        queryClient.invalidateQueries({ queryKey: [GET_VERIFICATION_EDITING_REQUESTS, farmId] });
        queryClient.invalidateQueries({ queryKey: [GET_FARM_REPORTING_YEAR, farmId] });
        // TODO: Only invalidate query for specific field, once chunking is implemented.
        queryClient.invalidateQueries({ queryKey: [GET_FIELD_TASKS] });
        queryClient.invalidateQueries({ queryKey: [GET_FARM_DATA_SUBMISSION_STATUS, farmId] });
      } else {
        queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICES, fieldId, simulationId] });
        queryClient.invalidateQueries({ queryKey: [GET_FIELDS_CROP_RECOMMENDATIONS, [fieldId], [simulationId]] });
      }
    },
  });
};

const mutate = ({ fieldId, cropId, simulationId, requestBody }: MutateParams) => {
  const isAdmin = getEnvironment().admin;

  if (isAdmin) {
    return updateFieldCropPracticeAdmin({ fieldId, cropId, requestBody });
  }

  if (simulationId) {
    return updateFieldCropPracticeSimulations({ fieldId, cropId, simulationId, requestBody });
  }

  return updateFieldCropPractice({ fieldId, cropId, requestBody });
};
